import React, { useEffect, useRef, useState, useCallback } from "react";
import "../styles/themes.css";

const MIN_WIDTH = 300;
const MAX_WIDTH_RATIO = 0.92;

// Viewport-aware default width
const getInitialWidth = () => {
  const vw = window.innerWidth;
  if (vw >= 1100) return 520;
  if (vw >= 768)  return Math.min(Math.floor(vw * 0.52), 460);
  return vw; // mobile: CSS controls layout as bottom sheet
};

const SECTIONS_ORDER = [
  "profile", "experience", "projects", "skills", "education",
  "contact", "certifications", "associative", "testimonials", "interests",
];

const SECTION_TITLES = {
  profile:        "About Me",
  experience:     "Experience",
  projects:       "Projects",
  skills:         "Skills",
  education:      "Education",
  certifications: "Certifications",
  contact:        "Contact",
  interests:      "Interests",
  testimonials:   "Testimonials",
  associative:    "Associations",
};

const PLANET_NAMES = {
  profile:        "The Sun",
  experience:     "Mercury",
  projects:       "Moon",
  skills:         "Venus",
  education:      "Mars",
  certifications: "Jupiter",
  contact:        "Earth",
  interests:      "Neptune",
  testimonials:   "Uranus",
  associative:    "Saturn",
};

const SidePanel = ({ section, onClose, onNavigate, children }) => {
  const themeClass = `theme-${section}`;
  const idx = SECTIONS_ORDER.indexOf(section);
  const prevSection = SECTIONS_ORDER[(idx - 1 + SECTIONS_ORDER.length) % SECTIONS_ORDER.length];
  const nextSection = SECTIONS_ORDER[(idx + 1) % SECTIONS_ORDER.length];
  const contentRef = useRef(null);

  // ── Resize ──────────────────────────────────────────────────────────────
  const [panelWidth, setPanelWidth] = useState(getInitialWidth);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  // Track viewport changes (orientation flip, window resize)
  useEffect(() => {
    const onViewportChange = () => {
      const vw = window.innerWidth;
      setIsMobile(vw < 768);
      // Re-clamp width if viewport shrinks
      if (vw >= 768) {
        setPanelWidth((w) => Math.min(w, Math.floor(vw * MAX_WIDTH_RATIO)));
      }
    };
    window.addEventListener("resize", onViewportChange);
    return () => window.removeEventListener("resize", onViewportChange);
  }, []);

  const startDrag = useCallback((clientX) => {
    isDragging.current = true;
    dragStartX.current = clientX;
    dragStartWidth.current = panelWidth;
  }, [panelWidth]);

  const onHandleMouseDown = useCallback((e) => {
    e.preventDefault();
    startDrag(e.clientX);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [startDrag]);

  const onHandleTouchStart = useCallback((e) => {
    startDrag(e.touches[0].clientX);
  }, [startDrag]);

  useEffect(() => {
    const clampWidth = (raw) => {
      const maxW = Math.floor(window.innerWidth * MAX_WIDTH_RATIO);
      return Math.min(Math.max(raw, MIN_WIDTH), maxW);
    };

    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      setPanelWidth(clampWidth(dragStartWidth.current + (dragStartX.current - e.clientX)));
    };
    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    const onTouchMove = (e) => {
      if (!isDragging.current) return;
      setPanelWidth(clampWidth(dragStartWidth.current + (dragStartX.current - e.touches[0].clientX)));
    };
    const onTouchEnd = () => { isDragging.current = false; };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  // Scroll to top whenever the section changes
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [section]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape")     onClose();
      // Arrow keys navigate AND fly to the corresponding planet
      if (e.key === "ArrowLeft")  onNavigate?.(prevSection, PLANET_NAMES[prevSection]);
      if (e.key === "ArrowRight") onNavigate?.(nextSection, PLANET_NAMES[nextSection]);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onNavigate, prevSection, nextSection]);

  return (
    <div
      className={`side-panel ${themeClass}`}
      role="complementary"
      aria-label={SECTION_TITLES[section]}
      style={isMobile ? {} : { width: panelWidth }}
    >
      {/* Drag-to-resize handle — hidden on mobile via CSS */}
      <div
        className="side-panel__resize-handle"
        onMouseDown={onHandleMouseDown}
        onTouchStart={onHandleTouchStart}
      />

      {/* Ambient glow blob */}
      <div className="side-panel__glow" />

      {/* Header */}
      <div className="side-panel__header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="side-panel__planet-label">{PLANET_NAMES[section]}</p>
          <h2 className="side-panel__title">{SECTION_TITLES[section]}</h2>
        </div>
        <button className="side-panel__close" onClick={onClose} aria-label="Close panel">
          ×
        </button>
      </div>

      {/* Prev / Next — clicking flies camera to the adjacent planet */}
      <div className="side-panel__nav">
        <button
          className="side-panel__nav-btn"
          onClick={() => onNavigate?.(prevSection, PLANET_NAMES[prevSection])}
          aria-label="Previous section"
        >
          ← {SECTION_TITLES[prevSection]}
        </button>
        <button
          className="side-panel__nav-btn"
          onClick={() => onNavigate?.(nextSection, PLANET_NAMES[nextSection])}
          aria-label="Next section"
        >
          {SECTION_TITLES[nextSection]} →
        </button>
      </div>

      {/* Scrollable content */}
      <div className="side-panel__content" ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

export default SidePanel;
